// === SHARED: feedback ===
// A tiny, dependency-free TACTILE-feedback helper for the cross-window "remote"
// programs (PatchBay, Transmitter, PowerMonitor). The remote rigs are meant to
// feel like physical control surfaces, so every meaningful interaction should
// answer back with a short click/buzz under the finger.
//
// tactile({ kind, sound }) does two independent things:
//   1. HAPTIC — fires navigator.vibrate(...) with a short distinct pattern per
//      control kind, when the platform supports it. Strictly feature-guarded:
//      typeof navigator.vibrate === "function". Most desktops have no vibration
//      motor, so this is simply a no-op there — that's fine.
//   2. AUDIO — plays a very short, very quiet WebAudio blip (a click / detent /
//      beep, shaped per kind) through this module's OWN little AudioContext.
//      It deliberately does NOT touch the audioBus / music graph, so it never
//      claims, pauses, or ducks any playing source. The blip honors a global
//      mute: pass sound:false (threaded from the Annex's soundEnabled flag) and
//      the audio half is skipped — the haptic half may still fire.
//
// Design notes:
//  - No React, no DOM. One lazily-created AudioContext, reused across calls.
//  - Null-safe + fully guarded: in a non-browser / no-WebAudio environment this
//    degrades to a silent no-op and never throws.
//  - Blips are intentionally tiny (a few ms .. ~70ms) and low-gain so a fast
//    flurry of clicks stays subtle rather than turning into noise.

// ---- Haptic patterns (milliseconds), one per control kind. -----------------
// Kept short + distinct so the hand can tell a switch flip from a button press.
const HAPTIC_PATTERNS = {
  switch: [12],            // a single crisp throw
  detent: [6],             // a faint notch as a knob steps
  button: [16],            // a firmer press
  surge: [10, 30, 18],     // a rolling jolt for the one-shot surge
  load: [8, 20],           // a soft two-step chunk
  transmit: [14, 24, 14],  // a punchier launch
};

// ---- Audio shapes, one per control kind. -----------------------------------
// freq = tone in Hz, dur = length in seconds, gain = peak level (kept low),
// type = oscillator waveform. "square" reads as a harder click; "sine" softer.
const AUDIO_SHAPES = {
  switch: { freq: 320, dur: 0.022, gain: 0.05, type: "square" },
  detent: { freq: 540, dur: 0.014, gain: 0.035, type: "square" },
  button: { freq: 240, dur: 0.03, gain: 0.06, type: "square" },
  surge: { freq: 150, dur: 0.07, gain: 0.07, type: "sawtooth" },
  load: { freq: 300, dur: 0.028, gain: 0.05, type: "triangle" },
  transmit: { freq: 460, dur: 0.05, gain: 0.06, type: "sine" },
};

// One shared AudioContext for ALL blips, created lazily on first real use so we
// never spin one up just by importing this module. Separate from audioBus.
let _ctx = null;

function getCtx() {
  if (typeof window === "undefined") return null;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (typeof Ctor !== "function") return null;
  try {
    if (_ctx == null) _ctx = new Ctor();
    // Some browsers start the context suspended until a user gesture. Every
    // tactile() call rides a real interaction, so a resume here is safe.
    if (_ctx.state === "suspended" && typeof _ctx.resume === "function") {
      _ctx.resume().catch(function () {});
    }
    return _ctx;
  } catch (e) {
    return null;
  }
}

// Fire the haptic motor for a kind, if the platform exposes one.
function buzz(kind) {
  if (typeof navigator === "undefined") return;
  if (typeof navigator.vibrate !== "function") return; // FEATURE-GUARD
  const pattern = HAPTIC_PATTERNS[kind] || HAPTIC_PATTERNS.button;
  try {
    navigator.vibrate(pattern);
  } catch (e) {
    /* ignore — vibration is best-effort */
  }
}

// Play one short, quiet blip for a kind through our own context.
function blip(kind) {
  const ctx = getCtx();
  if (!ctx) return;
  const shape = AUDIO_SHAPES[kind] || AUDIO_SHAPES.button;
  try {
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const amp = ctx.createGain();
    osc.type = shape.type;
    osc.frequency.setValueAtTime(shape.freq, now);
    // A quick attack + exponential decay reads as a tactile "click" rather than
    // a sustained tone. Ramp to a tiny floor (not 0) so the exp ramp is legal.
    amp.gain.setValueAtTime(0.0001, now);
    amp.gain.exponentialRampToValueAtTime(shape.gain, now + 0.004);
    amp.gain.exponentialRampToValueAtTime(0.0001, now + shape.dur);
    osc.connect(amp);
    amp.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + shape.dur + 0.02);
    // Let the nodes be collected once they've finished sounding.
    osc.onended = function () {
      try {
        osc.disconnect();
        amp.disconnect();
      } catch (e) {
        /* ignore */
      }
    };
  } catch (e) {
    /* ignore — audio is best-effort */
  }
}

// ---- Public API ------------------------------------------------------------
// tactile({ kind, sound }) — give one tactile answer for a control interaction.
//   kind  : one of "switch" | "detent" | "button" | "surge" | "load" | "transmit"
//   sound : when false, SKIP the audio blip (global mute). Haptic still fires.
//           Defaults to true so callers that don't thread it still get audio.
export function tactile(opts) {
  const o = opts || {};
  const kind = typeof o.kind === "string" ? o.kind : "button";
  buzz(kind);
  if (o.sound !== false) blip(kind);
}

// ===========================================================================
// ADDITIVE: payoff({ kind, sound }) — a BIGGER, more spectacular cousin of
// tactile(), for the rare "ACTIVATION" moments (TRANSMIT / SURGE) that deserve
// a real reward rather than a click. Everything above is untouched.
//
// Where tactile() is a single short blip, payoff() fires:
//   1. HAPTIC — a longer, rolling vibration pattern (a felt "WHUMP" + tail),
//      still strictly feature-guarded so it's a silent no-op on most desktops.
//   2. AUDIO  — a layered ~0.9s burst: a falling/rising tone SWEEP, a low
//      sub "thud" body, and a short filtered-noise crack on top, so the ear
//      reads it as a powerful discharge rather than a beep. Still routed
//      through THIS module's own context (never the music graph) and still
//      honors the global mute via sound:false.
//
// It is the audio/haptic half of the visual eruption the PowerMonitor shows;
// callers pair it with a windowBus broadcast so every window erupts together.
// Like everything here it is fully guarded and degrades to a no-op.
// ---------------------------------------------------------------------------

// Longer rolling haptic patterns for the big moments. Distinct per kind so a
// transmit launch and a power surge still feel different in the hand.
const PAYOFF_HAPTICS = {
  surge: [22, 18, 40, 14, 70],      // stutter then a long discharge
  transmit: [30, 12, 26, 12, 60],   // a punchier multi-stage launch
};

// Per-kind shape for the layered burst. start/end = sweep endpoints in Hz.
const PAYOFF_BURSTS = {
  surge: { start: 220, end: 60, dur: 0.85, gain: 0.16, sub: 48, type: "sawtooth" },
  transmit: { start: 180, end: 760, dur: 0.8, gain: 0.15, sub: 70, type: "square" },
};

// Honor prefers-reduced-motion for the AUDIO intensity too: a calmer, shorter,
// quieter burst (still clearly "something big happened", just not a wall of it).
function prefersReducedMotion() {
  try {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch (e) {
    return false;
  }
}

// A short burst of band-limited noise — reads as the "crack" of a discharge.
function noiseCrack(ctx, now, gain, dur) {
  try {
    const frames = Math.max(1, Math.floor(ctx.sampleRate * dur));
    const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < frames; i++) {
      // Decaying white noise so the crack has a sharp head and quick tail.
      data[i] = (Math.random() * 2 - 1) * (1 - i / frames);
    }
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const amp = ctx.createGain();
    amp.gain.setValueAtTime(gain, now);
    amp.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    // A high-pass keeps it a "tsss" crack rather than a muddy thump.
    let node = src;
    if (typeof ctx.createBiquadFilter === "function") {
      const hp = ctx.createBiquadFilter();
      hp.type = "highpass";
      hp.frequency.setValueAtTime(1400, now);
      src.connect(hp);
      node = hp;
    }
    node.connect(amp);
    amp.connect(ctx.destination);
    src.start(now);
    src.stop(now + dur + 0.02);
    src.onended = function () {
      try {
        src.disconnect();
        amp.disconnect();
      } catch (e) {
        /* ignore */
      }
    };
  } catch (e) {
    /* ignore — the noise layer is best-effort */
  }
}

// Play the layered activation burst for a kind through our own context.
function burst(kind) {
  const ctx = getCtx();
  if (!ctx) return;
  const base = PAYOFF_BURSTS[kind] || PAYOFF_BURSTS.surge;
  const calm = prefersReducedMotion();
  // Reduced-motion: shorter + quieter, and we skip the abrasive noise crack.
  const dur = calm ? Math.min(base.dur, 0.4) : base.dur;
  const gain = calm ? base.gain * 0.5 : base.gain;
  try {
    const now = ctx.currentTime;

    // 1) The sweeping lead tone — the "voice" of the discharge.
    const osc = ctx.createOscillator();
    const amp = ctx.createGain();
    osc.type = base.type;
    osc.frequency.setValueAtTime(base.start, now);
    if (typeof osc.frequency.exponentialRampToValueAtTime === "function") {
      osc.frequency.exponentialRampToValueAtTime(Math.max(20, base.end), now + dur);
    } else {
      osc.frequency.linearRampToValueAtTime(Math.max(20, base.end), now + dur);
    }
    amp.gain.setValueAtTime(0.0001, now);
    amp.gain.exponentialRampToValueAtTime(gain, now + 0.012);
    amp.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    osc.connect(amp);
    amp.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + dur + 0.03);
    osc.onended = function () {
      try {
        osc.disconnect();
        amp.disconnect();
      } catch (e) {
        /* ignore */
      }
    };

    // 2) A low sub "body" so the burst has weight on capable outputs.
    const sub = ctx.createOscillator();
    const subAmp = ctx.createGain();
    sub.type = "sine";
    sub.frequency.setValueAtTime(base.sub, now);
    sub.frequency.exponentialRampToValueAtTime(Math.max(20, base.sub * 0.5), now + dur * 0.8);
    subAmp.gain.setValueAtTime(0.0001, now);
    subAmp.gain.exponentialRampToValueAtTime(gain * 0.9, now + 0.02);
    subAmp.gain.exponentialRampToValueAtTime(0.0001, now + dur * 0.9);
    sub.connect(subAmp);
    subAmp.connect(ctx.destination);
    sub.start(now);
    sub.stop(now + dur + 0.03);
    sub.onended = function () {
      try {
        sub.disconnect();
        subAmp.disconnect();
      } catch (e) {
        /* ignore */
      }
    };

    // 3) The noise crack on top — skipped in the calmer reduced-motion mode.
    if (!calm) noiseCrack(ctx, now, gain * 0.8, 0.16);
  } catch (e) {
    /* ignore — audio is best-effort */
  }
}

// Fire the bigger rolling haptic, if the platform exposes a motor.
function bigBuzz(kind) {
  if (typeof navigator === "undefined") return;
  if (typeof navigator.vibrate !== "function") return; // FEATURE-GUARD
  const pattern = PAYOFF_HAPTICS[kind] || PAYOFF_HAPTICS.surge;
  try {
    navigator.vibrate(pattern);
  } catch (e) {
    /* ignore — vibration is best-effort */
  }
}

// payoff({ kind, sound }) — the spectacular activation feedback.
//   kind  : "surge" | "transmit" (falls back to "surge").
//   sound : when false, SKIP the audio burst (global mute). Haptic still fires.
export function payoff(opts) {
  const o = opts || {};
  const kind = typeof o.kind === "string" ? o.kind : "surge";
  bigBuzz(kind);
  if (o.sound !== false) burst(kind);
}

// ===========================================================================
// ADDITIVE: tvTactile({ kind, sound }) — a CRT-TELEVISION flavored cousin of
// tactile(), for the DeadChannel set's chunky skeuomorphic controls. Everything
// above is untouched; this only ADDS new exports + private helpers.
//
// The dead-channel TV wants feedback that reads as PHYSICAL HARDWARE: a heavy
// power THUNK, a chunky channel-knob DETENT with a burst of analog STATIC when
// the channel changes, fine knob TICKS as a dial sweeps, and a low DEGAUSS HUM.
// Like tactile()/payoff() it does two independent things:
//   1. HAPTIC — navigator.vibrate(...) with a short distinct pattern per kind,
//      strictly feature-guarded (no-op on most desktops).
//   2. AUDIO  — a short synthesized cue shaped per kind, routed through THIS
//      module's OWN AudioContext (never the music graph), honoring the global
//      mute via sound:false (haptic may still fire when muted).
// Fully guarded; degrades to a silent no-op and never throws.
// ---------------------------------------------------------------------------

// Haptic patterns for the TV controls (milliseconds). Distinct so the hand can
// tell a power THUNK from a knob tick from a static-laced channel change.
const TV_HAPTICS = {
  power: [18, 26, 12],        // a heavy two-stage THUNK
  channel: [10, 14],          // detent + the snap into the next channel
  knobTick: [5],              // a fine notch as a dial sweeps
  degauss: [12, 40, 20, 60],  // a wobbling magnetic swell
  // --- ADDITIVE kinds for the iterated DeadChannel (guide / scan / antenna / lock) ---
  uiBeep: [7],                // a soft OSD-menu blip (open / move a cursor)
  scan: [8, 8, 8],            // a stuttering auto-tune sweep
  reception: [4],             // a faint crackle as the antenna is dragged
  lock: [16, 30, 16, 60],     // the eerie "lock" when the lost channel is found
};

// One short, quiet WebAudio cue per TV control kind. Each builds its own tiny
// graph through getCtx() and tears it down on end. Kept brief + low-gain.
function tvCue(kind) {
  const ctx = getCtx();
  if (!ctx) return;
  const calm = prefersReducedMotion();
  try {
    const now = ctx.currentTime;
    if (kind === "power") {
      // Heavy THUNK: a low sine that drops in pitch, weighted by a sub.
      const osc = ctx.createOscillator();
      const amp = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(130, now);
      osc.frequency.exponentialRampToValueAtTime(42, now + 0.12);
      amp.gain.setValueAtTime(0.0001, now);
      amp.gain.exponentialRampToValueAtTime(0.12, now + 0.006);
      amp.gain.exponentialRampToValueAtTime(0.0001, now + 0.16);
      osc.connect(amp);
      amp.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.2);
      osc.onended = function () {
        try { osc.disconnect(); amp.disconnect(); } catch (e) { /* ignore */ }
      };
      // A faint click "snap" of the mechanical switch on top.
      tvClick(ctx, now, 0.05);
      return;
    }
    if (kind === "channel") {
      // A chunky detent CLICK plus a short burst of analog STATIC / white-noise
      // pop — the sound of the channel snapping to a new feed.
      tvClick(ctx, now, 0.06);
      tvStatic(ctx, now + 0.012, calm ? 0.03 : 0.07, calm ? 0.05 : 0.09);
      return;
    }
    if (kind === "knobTick") {
      // A single fine, high, very short tick — a knob notch.
      const osc = ctx.createOscillator();
      const amp = ctx.createGain();
      osc.type = "square";
      osc.frequency.setValueAtTime(620, now);
      amp.gain.setValueAtTime(0.0001, now);
      amp.gain.exponentialRampToValueAtTime(0.03, now + 0.002);
      amp.gain.exponentialRampToValueAtTime(0.0001, now + 0.012);
      osc.connect(amp);
      amp.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.03);
      osc.onended = function () {
        try { osc.disconnect(); amp.disconnect(); } catch (e) { /* ignore */ }
      };
      return;
    }
    if (kind === "degauss") {
      // A low magnetic HUM that swells then settles — the degauss coil.
      const dur = calm ? 0.35 : 0.7;
      const osc = ctx.createOscillator();
      const amp = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(70, now);
      osc.frequency.linearRampToValueAtTime(58, now + dur);
      amp.gain.setValueAtTime(0.0001, now);
      amp.gain.exponentialRampToValueAtTime(calm ? 0.05 : 0.08, now + 0.06);
      amp.gain.exponentialRampToValueAtTime(0.0001, now + dur);
      // Slow amplitude wobble so the hum "breathes" like a swinging field.
      const lfo = ctx.createOscillator();
      const lfoAmp = ctx.createGain();
      lfo.type = "sine";
      lfo.frequency.setValueAtTime(7, now);
      lfoAmp.gain.setValueAtTime(0.03, now);
      lfo.connect(lfoAmp);
      lfoAmp.connect(amp.gain);
      osc.connect(amp);
      amp.connect(ctx.destination);
      osc.start(now);
      lfo.start(now);
      osc.stop(now + dur + 0.02);
      lfo.stop(now + dur + 0.02);
      osc.onended = function () {
        try {
          osc.disconnect(); amp.disconnect();
          lfo.disconnect(); lfoAmp.disconnect();
        } catch (e) { /* ignore */ }
      };
      return;
    }
    if (kind === "uiBeep") {
      // A soft, short OSD-menu blip — moving a cursor / opening the GUIDE.
      const osc = ctx.createOscillator();
      const amp = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(880, now);
      osc.frequency.exponentialRampToValueAtTime(990, now + 0.04);
      amp.gain.setValueAtTime(0.0001, now);
      amp.gain.exponentialRampToValueAtTime(0.03, now + 0.004);
      amp.gain.exponentialRampToValueAtTime(0.0001, now + 0.05);
      osc.connect(amp);
      amp.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.07);
      osc.onended = function () {
        try { osc.disconnect(); amp.disconnect(); } catch (e) { /* ignore */ }
      };
      return;
    }
    if (kind === "scan") {
      // AUTO-TUNE sweep: three quick detent clicks laced with thin static —
      // the set racing past frequencies looking for a lock.
      tvClick(ctx, now, 0.045);
      tvStatic(ctx, now + 0.01, calm ? 0.03 : 0.06, calm ? 0.04 : 0.06);
      tvClick(ctx, now + 0.09, 0.04);
      tvClick(ctx, now + 0.17, 0.045);
      return;
    }
    if (kind === "reception") {
      // A faint analog CRACKLE as the antenna is dragged — a tiny static puff.
      tvStatic(ctx, now, calm ? 0.02 : 0.035, calm ? 0.03 : 0.05);
      return;
    }
    if (kind === "lock") {
      // The LOST-CHANNEL lock: a rising, slightly detuned shimmer that resolves
      // out of static — reads as "something tuned in that shouldn't be here".
      tvStatic(ctx, now, calm ? 0.06 : 0.14, calm ? 0.05 : 0.08);
      const osc = ctx.createOscillator();
      const amp = ctx.createGain();
      const dur = calm ? 0.4 : 0.75;
      osc.type = "sine";
      osc.frequency.setValueAtTime(180, now);
      osc.frequency.exponentialRampToValueAtTime(620, now + dur);
      amp.gain.setValueAtTime(0.0001, now + 0.04);
      amp.gain.exponentialRampToValueAtTime(calm ? 0.05 : 0.08, now + 0.12);
      amp.gain.exponentialRampToValueAtTime(0.0001, now + dur);
      // a detuned partial a few Hz off for an unsettling beat
      const osc2 = ctx.createOscillator();
      osc2.type = "sine";
      osc2.frequency.setValueAtTime(184, now);
      osc2.frequency.exponentialRampToValueAtTime(628, now + dur);
      osc2.connect(amp);
      osc.connect(amp);
      amp.connect(ctx.destination);
      osc.start(now);
      osc2.start(now);
      osc.stop(now + dur + 0.03);
      osc2.stop(now + dur + 0.03);
      osc.onended = function () {
        try { osc.disconnect(); osc2.disconnect(); amp.disconnect(); } catch (e) { /* ignore */ }
      };
      return;
    }
    // Fallback: a plain detent click.
    tvClick(ctx, now, 0.05);
  } catch (e) {
    /* ignore — audio is best-effort */
  }
}

// A chunky mechanical CLICK — a short square blip with a fast attack/decay.
function tvClick(ctx, now, gain) {
  try {
    const osc = ctx.createOscillator();
    const amp = ctx.createGain();
    osc.type = "square";
    osc.frequency.setValueAtTime(180, now);
    osc.frequency.exponentialRampToValueAtTime(90, now + 0.02);
    amp.gain.setValueAtTime(0.0001, now);
    amp.gain.exponentialRampToValueAtTime(gain, now + 0.003);
    amp.gain.exponentialRampToValueAtTime(0.0001, now + 0.03);
    osc.connect(amp);
    amp.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.05);
    osc.onended = function () {
      try { osc.disconnect(); amp.disconnect(); } catch (e) { /* ignore */ }
    };
  } catch (e) {
    /* ignore */
  }
}

// A short burst of TV STATIC — full-band decaying white noise, the "pop" of
// snow when a channel changes. Like noiseCrack but broadband (no high-pass) so
// it reads as analog hiss rather than a sharp tsss.
function tvStatic(ctx, now, dur, gain) {
  try {
    const frames = Math.max(1, Math.floor(ctx.sampleRate * dur));
    const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < frames; i++) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / frames);
    }
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const amp = ctx.createGain();
    amp.gain.setValueAtTime(gain, now);
    amp.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    src.connect(amp);
    amp.connect(ctx.destination);
    src.start(now);
    src.stop(now + dur + 0.02);
    src.onended = function () {
      try { src.disconnect(); amp.disconnect(); } catch (e) { /* ignore */ }
    };
  } catch (e) {
    /* ignore — the static layer is best-effort */
  }
}

// Fire the TV haptic for a kind, if the platform exposes a motor.
function tvBuzz(kind) {
  if (typeof navigator === "undefined") return;
  if (typeof navigator.vibrate !== "function") return; // FEATURE-GUARD
  const pattern = TV_HAPTICS[kind] || TV_HAPTICS.channel;
  try {
    navigator.vibrate(pattern);
  } catch (e) {
    /* ignore — vibration is best-effort */
  }
}

// tvTactile({ kind, sound }) — tactile feedback for the DeadChannel TV controls.
//   kind  : "power" | "channel" | "knobTick" | "degauss" | "uiBeep" | "scan"
//           | "reception" | "lock" (falls back to a click).
//   sound : when false, SKIP the audio cue (global mute). Haptic still fires.
export function tvTactile(opts) {
  const o = opts || {};
  const kind = typeof o.kind === "string" ? o.kind : "channel";
  tvBuzz(kind);
  if (o.sound !== false) tvCue(kind);
}

export default tactile;
